// Полноэкранный просмотр фото с зумом и жестами (PhotoSwipe).
// Библиотека и её CSS грузятся по первому клику — в основной бандл не попадают

import { getImageEntry, variantUrl } from '@shared/lib/responsive-images';

import { isChunkLoadError, reloadOnce } from './reloadOnce';

interface LightboxPhoto {
  src: string;
  width: number;
  height: number;
  alt?: string;
  msrc?: string;
}

const loadSize = (src: string) =>
  new Promise<LightboxPhoto>((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ src, width: img.naturalWidth, height: img.naturalHeight });
    // Битое фото не должно ломать галерею — PhotoSwipe покажет errorMsg
    img.onerror = () => resolve({ src, width: 1200, height: 900 });
    img.src = src;
  });

const resolvePhoto = (photo: string | LightboxPhoto): Promise<LightboxPhoto> => {
  const src = typeof photo === 'string' ? photo : photo.src;
  const entry = getImageEntry(src);
  const largest = entry?.widths.at(-1);
  if (entry && largest) {
    // Если исходник больше 1600px, сохраняем его для зума. Иначе открываем самый крупный WebP.
    const useOriginal = entry.width > largest;
    const width = useOriginal ? entry.width : largest;
    return Promise.resolve({
      ...(typeof photo === 'string' ? {} : photo),
      src: useOriginal ? src : variantUrl(entry.base, largest, 'webp'),
      width,
      height: Math.round((entry.height * width) / entry.width),
      msrc: variantUrl(entry.base, entry.widths[0], 'webp'),
    });
  }
  return typeof photo === 'string' ? loadSize(photo) : Promise.resolve(photo);
};

export const openLightbox = async (
  photos: readonly (string | LightboxPhoto)[],
  index: number,
  alt: string,
) => {
  let loaded;
  try {
    loaded = await Promise.all([
      import('photoswipe'),
      // Манифест даёт размеры без предварительного скачивания всех кадров каталога.
      Promise.all(photos.map(resolvePhoto)),
      import('photoswipe/style.css'),
    ]);
  } catch (error) {
    // Код галереи не загрузился — чаще всего вкладка открыта до деплоя. Глобальный обработчик
    // vite:preloadError молчит, пока идёт фоновая докачка страниц, поэтому перезагружаем сами;
    // вызывают через void — без catch промис падал бы необработанным
    if (isChunkLoadError(error)) reloadOnce();
    else console.error(error);
    return;
  }
  const [{ default: PhotoSwipe }, slides] = loaded;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  new PhotoSwipe({
    dataSource: slides.map((s) => ({ ...s, alt: s.alt ?? alt })),
    index,
    bgOpacity: 0.92,
    showHideAnimationType: reducedMotion ? 'none' : 'fade',
    closeTitle: 'Закрыть',
    zoomTitle: 'Увеличить',
    arrowPrevTitle: 'Предыдущее фото',
    arrowNextTitle: 'Следующее фото',
    indexIndicatorSep: ' из ',
    errorMsg: 'Не удалось загрузить фото',
  }).init();
};
